import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaCommentDots, FaTimes, FaCheckCircle } from 'react-icons/fa';
import api from '../services/api';
import './BetaFeedbackWidget.css';

const FEEDBACK_TYPES = [
  { value: 'bug', label: '🐛 Bug / something broken' },
  { value: 'idea', label: '💡 Idea / feature request' },
  { value: 'general', label: '💬 General feedback' },
];

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

const BetaFeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotError, setScreenshotError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const reset = () => {
    setFeedbackType('general');
    setSubject('');
    setBody('');
    setScreenshot(null);
    setScreenshotError('');
    setError('');
    setSubmitted(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const handleScreenshot = (e) => {
    const file = e.target.files && e.target.files[0];
    setScreenshotError('');
    if (!file) {
      setScreenshot(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setScreenshotError('Screenshot must be an image.');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setScreenshotError('Screenshot must be 5 MB or smaller.');
      return;
    }
    setScreenshot(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!subject.trim() || !body.trim()) {
      setError('Subject and details are both required.');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('feedback_type', feedbackType);
      form.append('subject', subject.trim());
      form.append('body', body.trim());
      form.append('page_url', `${window.location.origin}${location.pathname}${location.search}`);
      if (screenshot) form.append('screenshot', screenshot);

      await api.post('/beta-feedback/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail
        || err.response?.data?.subject?.[0]
        || err.response?.data?.body?.[0]
        || 'Could not send feedback. Please try again.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="beta-feedback-pill"
        onClick={() => setOpen(true)}
        aria-label="Send beta feedback"
      >
        <FaCommentDots aria-hidden="true" />
        <span>Beta · Feedback</span>
      </button>

      {open && (
        <div className="beta-feedback-overlay" onClick={close} role="presentation">
          <div
            className="beta-feedback-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-feedback-title"
          >
            <button
              type="button"
              className="beta-feedback-close"
              onClick={close}
              aria-label="Close feedback form"
            >
              <FaTimes />
            </button>

            {submitted ? (
              <div className="beta-feedback-success">
                <FaCheckCircle className="beta-feedback-success-icon" aria-hidden="true" />
                <h3>Thanks — we got it.</h3>
                <p>
                  Pop1 is in beta and your note genuinely shapes what we ship next.
                  We&apos;ll read it personally.
                </p>
                <button type="button" className="beta-feedback-primary" onClick={close}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="beta-feedback-form">
                <h3 id="beta-feedback-title">Send beta feedback</h3>
                <p className="beta-feedback-lede">
                  Bug? Idea? Something confusing? Drop it below — goes straight to the team.
                </p>

                <label className="beta-feedback-label">
                  Type
                  <select
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="beta-feedback-input"
                  >
                    {FEEDBACK_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>

                <label className="beta-feedback-label">
                  Subject
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Short summary"
                    maxLength={200}
                    className="beta-feedback-input"
                    required
                  />
                </label>

                <label className="beta-feedback-label">
                  Details
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="What happened, what you expected, anything that'd help us repro…"
                    rows={5}
                    className="beta-feedback-input"
                    required
                  />
                </label>

                <label className="beta-feedback-label">
                  Screenshot (optional)
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshot}
                    className="beta-feedback-input beta-feedback-file"
                  />
                </label>
                {screenshotError && <p className="beta-feedback-error">{screenshotError}</p>}
                {error && <p className="beta-feedback-error">{error}</p>}

                <div className="beta-feedback-actions">
                  <button type="button" className="beta-feedback-secondary" onClick={close}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="beta-feedback-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BetaFeedbackWidget;
